import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Footer, MarketingHeader, SEO } from '@/components';
import { track } from '@/lib/analytics';

const TOOLS = [
  {
    name: 'list_households',
    description: 'Households, co-parent first names, and children for the signed-in parent.',
  },
  {
    name: 'who_has_the_kids',
    description: 'Who has each child on a given date (defaults to today).',
  },
  {
    name: 'get_custody',
    description: 'Day-by-day custody assignments for a date range.',
  },
  {
    name: 'get_next_exchange',
    description: 'The next upcoming custody pickup or dropoff.',
  },
  {
    name: 'list_calendar_events',
    description: 'Shared appointments and activities on the household calendar.',
  },
  {
    name: 'get_expense_balance',
    description: 'How much you are owed or owe your co-parent.',
  },
  {
    name: 'list_expenses',
    description: 'Recent child-related expenses, optionally filtered by date or status.',
  },
  {
    name: 'list_exchange_logs',
    description: 'Logged exchanges (on time, late, or no-show).',
  },
  {
    name: 'get_unread_summary',
    description: 'Unread count on the household’s main Harbor chat.',
  },
  {
    name: 'list_recent_messages',
    description: 'The most recent main-chat messages (text only).',
  },
];

function ConnectPage() {
  useEffect(() => {
    track('Harbor Connector Screen');
  }, []);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Harbor Connector for Muse and AI agents'
        description='Connect Harbor to Muse so your personal AI can read your custody calendar, expenses, and messages — acting only for you, never as your co-parent.'
        path='/connect'
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-3xl px-6'>
          <h1 className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'>
            Harbor Connector
          </h1>
          <p className='text-xl leading-relaxed text-gray-600'>
            Ask Muse (or another personal agent) about your Harbor household. The
            connector acts only for the parent who signed in. It cannot read or
            act as your co-parent.
          </p>
        </div>
      </section>

      <section className='py-16'>
        <div className='mx-auto max-w-3xl space-y-10 px-6'>
          <div>
            <h2 className='mb-3 text-2xl font-bold text-gray-900'>What it can do</h2>
            <p className='mb-6 text-gray-600 leading-relaxed'>
              v1 is read-only. Every answer comes from the same Harbor records you
              already see in the app. Nothing is sent to your co-parent outside Harbor.
            </p>
            <ul className='space-y-3'>
              {TOOLS.map((tool) => (
                <li key={tool.name} className='border-b border-gray-100 pb-3'>
                  <p className='font-mono text-sm text-primary'>{tool.name}</p>
                  <p className='text-gray-600'>{tool.description}</p>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className='mb-3 text-2xl font-bold text-gray-900'>Access</h2>
            <ul className='list-disc space-y-2 pl-5 text-gray-600 leading-relaxed'>
              <li>A free Harbor account on iOS, Android, or the web, and membership in a household.</li>
              <li>
                OAuth 2.1 with PKCE. The only connector scope in v1 is{' '}
                <code className='font-mono text-sm'>harbor:read</code>.
              </li>
              <li>US and Canada. About 60 tool calls per minute per parent.</li>
              <li>
                Endpoint:{' '}
                <code className='font-mono text-sm'>https://api.harborcoparent.com/mcp</code>
              </li>
            </ul>
          </div>

          <div>
            <h2 className='mb-3 text-2xl font-bold text-gray-900'>Revoke access</h2>
            <p className='text-gray-600 leading-relaxed'>
              Disconnect from Muse’s connector settings, or revoke the client from
              Harbor (GET/DELETE <code className='font-mono text-sm'>/v1/connectors</code>
              ). Harbor keeps a last-used record so you can see what is connected.
            </p>
          </div>

          <div>
            <h2 className='mb-3 text-2xl font-bold text-gray-900'>Privacy</h2>
            <p className='text-gray-600 leading-relaxed'>
              Harbor’s{' '}
              <Link to='/privacy-policy' className='text-primary font-semibold hover:underline'>
                privacy policy
              </Link>{' '}
              and{' '}
              <Link to='/terms-of-service' className='text-primary font-semibold hover:underline'>
                terms of service
              </Link>{' '}
              apply. Muse and other agents have their own policies for data they
              receive after you connect.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/connect')({
  component: ConnectPage,
});
